"use client";

import { ArrowUpRight } from "lucide-react";
import { motion, useMotionValue, useSpring } from "motion/react";
import type { PointerEvent, ReactNode } from "react";

import { trackAttributes } from "@/lib/analytics/beacon";
import { cn } from "@/lib/utils";

// =============================================================================

type NetworkCardProps = {
  href: string;
  name: string;
  username: string;
  description: string;
  year: number | null;
  icon: ReactNode;
  labels: { go: string; since: string | null; newTab: string };
};

const SPRING = { stiffness: 260, damping: 28, mass: 0.6 };

/**
 * Network card of /connect. On hover a soft light follows the pointer, the
 * border lights up, the logo lifts and the arrow is swapped by a copy coming
 * from the bottom left.
 */
export function NetworkCard({
  href,
  name,
  username,
  description,
  year,
  icon,
  labels,
}: NetworkCardProps) {
  const x = useSpring(useMotionValue(0), SPRING);
  const y = useSpring(useMotionValue(0), SPRING);

  const onPointerMove = (event: PointerEvent<HTMLAnchorElement>) => {
    if (event.pointerType !== "mouse") return;
    const rect = event.currentTarget.getBoundingClientRect();
    x.set(event.clientX - rect.left);
    y.set(event.clientY - rect.top);
  };

  return (
    <a
      {...trackAttributes("NETWORK_CLICK", { network: name, from: "connect" })}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onPointerMove={onPointerMove}
      onPointerEnter={(event) => {
        // Start the light under the pointer instead of sliding from a corner
        const rect = event.currentTarget.getBoundingClientRect();
        x.jump(event.clientX - rect.left);
        y.jump(event.clientY - rect.top);
      }}
      className={cn(
        "group/network relative isolate flex h-full min-h-40 items-stretch gap-4 overflow-hidden rounded-xl border border-foreground/10 p-4 pr-5",
        "bg-surface-1 shadow-[0_1px_0_rgb(255_255_255/0.6)_inset,0_10px_30px_-18px_rgb(0_0_0/0.25)] dark:bg-linear-to-b dark:from-surface-3 dark:to-surface-3/70 dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.05)]",
        "transition-[border-color,translate,box-shadow] duration-500 ease-out-expo hover:-translate-y-1 hover:border-foreground/25 hover:shadow-[0_24px_50px_-24px_rgb(0_0_0/0.45)]"
      )}
    >
      {/* Light following the pointer */}
      <motion.span
        aria-hidden
        style={{ x, y }}
        className="pointer-events-none absolute -top-36 -left-36 -z-10 size-72 rounded-full bg-[radial-gradient(circle,rgb(20_20_20/0.07),transparent_65%)] opacity-0 transition-opacity duration-500 group-hover/network:opacity-100 dark:bg-[radial-gradient(circle,rgb(255_255_255/0.11),transparent_65%)]"
      />

      <span className="grid w-14 shrink-0 place-items-center text-foreground transition-transform duration-600 ease-out-expo group-hover/network:-translate-y-0.5 group-hover/network:scale-110">
        {icon}
      </span>

      <span className="flex min-w-0 flex-1 flex-col pt-1">
        <span className="text-xl leading-tight font-semibold">{name}</span>
        <span className="mt-1 truncate text-sm text-muted-foreground">
          {username}
        </span>
        <span className="mt-3 text-base leading-snug text-foreground/90">
          {description}
        </span>

        <span className="mt-auto flex items-end justify-between gap-4 pt-4">
          <span className="text-sm text-muted-foreground tabular-nums">
            {year && (
              <>
                <span className="sr-only">{labels.since}</span>
                <span aria-hidden>{year}</span>
              </>
            )}
          </span>
          <span className="inline-flex items-center gap-1.5 text-base">
            {labels.go}
            <span className="relative block size-4 overflow-hidden">
              <ArrowUpRight
                aria-hidden
                className="absolute inset-0 size-4 transition-transform duration-500 ease-out-expo group-hover/network:translate-x-full group-hover/network:-translate-y-full"
              />
              <ArrowUpRight
                aria-hidden
                className="absolute inset-0 size-4 -translate-x-full translate-y-full transition-transform duration-500 ease-out-expo group-hover/network:translate-x-0 group-hover/network:translate-y-0"
              />
            </span>
            <span className="sr-only">{labels.newTab}</span>
          </span>
        </span>
      </span>
    </a>
  );
}

// =============================================================================
